import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Clock, MessageSquare, GitBranch } from "lucide-react";

const stats = [
  { icon: Zap,          value: 150,   suffix: "+",  label: "Automations Built",        sub: "Across all industries",         color: "blue" },
  { icon: Clock,        value: 10000, suffix: "+",  label: "Hours Saved",               sub: "Manual work eliminated",        color: "violet" },
  { icon: MessageSquare,value: 90,    suffix: "%",  label: "Faster Response Time",      sub: "With AI-powered support",       color: "blue" },
  { icon: GitBranch,    value: 50,    suffix: "+",  label: "AI-Powered Workflows",      sub: "Running 24/7 autonomously",     color: "violet" },
];

function useCounter(target, duration = 2200, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return count;
}

function StatCard({ stat, active, index }) {
  const count = useCounter(stat.value, 2200, active);
  const Icon = stat.icon;
  const isBlue = stat.color === "blue";

  const spotlightRef = useRef(null);
  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.div
      ref={spotlightRef}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMouseMove}
      className="spotlight-card gradient-border-card stat-card card-inner-top rounded-2xl p-7 cursor-default"
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-5 ${isBlue ? "bg-blue-500/10" : "bg-violet-500/10"}`}>
        <Icon className={`w-5 h-5 ${isBlue ? "text-blue-400" : "text-violet-400"}`} strokeWidth={1.5} />
      </div>

      {/* Value */}
      <div className="flex items-baseline gap-0.5 mb-2">
        <span className="text-[44px] font-black leading-none tracking-[-0.04em] text-white tabular-nums">
          {count.toLocaleString()}
        </span>
        <span className={`text-2xl font-black ml-0.5 leading-none ${isBlue ? "text-blue-400/80" : "text-violet-400/80"}`}>
          {stat.suffix}
        </span>
      </div>

      <p className="text-[14px] font-semibold text-white/80 mb-1 tracking-[-0.01em]">{stat.label}</p>
      <p className="text-[12px] text-white/30">{stat.sub}</p>
    </motion.div>
  );
}

export default function TrustStats() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.25 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section data-testid="trust-stats-section" ref={ref} className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[10.5px] font-mono tracking-[0.22em] text-white/20 uppercase mb-10"
        >
          Proven results
        </motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((s, i) => (
            <StatCard key={s.label} stat={s} active={active} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
